import { create } from "zustand";
import { persist } from "zustand/middleware";
import type { SiteUser } from "@gerama/types";

interface SiteAuthState {
  token: string | null;
  user: SiteUser | null;
  setSession: (token: string, user: SiteUser) => void;
  /** Replaces the cached profile after `GET /users/me` or a profile edit — keeps the token. No-op when signed out. */
  setUser: (user: SiteUser) => void;
  clearSession: () => void;
}

/**
 * The public site's signed-in session — deliberately different storage from
 * `packages/auth`'s admin cookie: the site's own auth (phone/OTP, Google)
 * has no privileged surface an XSS needs protecting from the way the admin
 * panel does, and every protected call the site makes (subscribe, playlist
 * mutations) happens from the browser, so the token has to be readable by
 * client code either way. Persisted to `localStorage` under one key,
 * survives a refresh, never sent anywhere but `Authorization` headers to
 * core-api.
 */
export const useSiteAuthStore = create<SiteAuthState>()(
  persist(
    (set) => ({
      token: null,
      user: null,
      setSession: (token, user) => set({ token, user }),
      setUser: (user) => set((state) => (state.token ? { user } : state)),
      clearSession: () => set({ token: null, user: null }),
    }),
    { name: "gerama.site.session" },
  ),
);
